/**
 * Key state for tracking keyboard input
 */
export interface KeyState {
  isDown: boolean;
  isUp: boolean;
  /** Was just pressed this frame */
  justDown: boolean;
  /** Was just released this frame */
  justUp: boolean;
  /** Key code */
  keyCode: string;
}

/**
 * Cursor keys state (arrow keys)
 */
export interface CursorKeys {
  up: KeyState;
  down: KeyState;
  left: KeyState;
  right: KeyState;
}

/**
 * Pointer (mouse) state
 */
export interface PointerState {
  x: number;
  y: number;
  worldX: number;
  worldY: number;
  isDown: boolean;
  leftButtonDown: boolean;
  rightButtonDown: boolean;
  button: number;
}

/**
 * Pointer event data passed to callbacks
 */
export interface PointerEventData {
  x: number;
  y: number;
  worldX: number;
  worldY: number;
  leftButtonDown: boolean;
  rightButtonDown: boolean;
  button: number;
  /** True if the click was on the game canvas, not on UI elements */
  isOnCanvas: boolean;
}

/**
 * Input manager for keyboard and mouse input.
 */
export class InputManager {
  private keys: Map<string, KeyState> = new Map();
  private keyDownSet: Set<string> = new Set();
  private justDownSet: Set<string> = new Set();
  private justUpSet: Set<string> = new Set();

  public readonly pointer: PointerState;
  private _enabled: boolean = true;
  private canvas: HTMLCanvasElement | null = null;

  // Callbacks for world position conversion
  private getWorldX: ((screenX: number) => number) | null = null;
  private getWorldY: ((screenY: number) => number) | null = null;

  // Pointer event callbacks
  private pointerDownCallbacks: Array<(data: PointerEventData) => void> = [];
  private pointerUpCallbacks: Array<(data: PointerEventData) => void> = [];
  private pointerMoveCallbacks: Array<(data: PointerEventData) => void> = [];

  constructor() {
    this.pointer = {
      x: 0,
      y: 0,
      worldX: 0,
      worldY: 0,
      isDown: false,
      leftButtonDown: false,
      rightButtonDown: false,
      button: -1,
    };

    // Set up keyboard listeners
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);

    // Set up mouse listeners
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mousedown', this.handleMouseDown);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('contextmenu', this.handleContextMenu);
  }

  /**
   * Set the canvas element for coordinate calculations
   */
  setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
  }

  /**
   * Set world position conversion functions (from Camera)
   */
  setWorldPositionConverter(
    getWorldX: (screenX: number) => number,
    getWorldY: (screenY: number) => number
  ): void {
    this.getWorldX = getWorldX;
    this.getWorldY = getWorldY;
  }

  /**
   * Enable or disable input
   */
  set enabled(value: boolean) {
    this._enabled = value;
  }

  get enabled(): boolean {
    return this._enabled;
  }

  /**
   * Add a key to track
   */
  addKey(keyCode: string): KeyState {
    const code = keyCode.toUpperCase();

    if (!this.keys.has(code)) {
      const state: KeyState = {
        isDown: false,
        isUp: true,
        justDown: false,
        justUp: false,
        keyCode: code,
      };
      this.keys.set(code, state);
    }

    return this.keys.get(code)!;
  }

  /**
   * Create cursor keys (arrow keys)
   */
  createCursorKeys(): CursorKeys {
    return {
      up: this.addKey('ARROWUP'),
      down: this.addKey('ARROWDOWN'),
      left: this.addKey('ARROWLEFT'),
      right: this.addKey('ARROWRIGHT'),
    };
  }

  /**
   * Check if a key is currently down
   */
  isKeyDown(keyCode: string): boolean {
    const code = keyCode.toUpperCase();
    return this.keyDownSet.has(code);
  }

  /**
   * Check if a key was just pressed this frame
   */
  isKeyJustDown(keyCode: string): boolean {
    const code = keyCode.toUpperCase();
    return this.justDownSet.has(code);
  }

  /**
   * Check if a key was just released this frame
   */
  isKeyJustUp(keyCode: string): boolean {
    const code = keyCode.toUpperCase();
    return this.justUpSet.has(code);
  }

  /**
   * Get key state
   */
  getKey(keyCode: string): KeyState | undefined {
    return this.keys.get(keyCode.toUpperCase());
  }

  /**
   * Register a callback for pointer down events
   */
  onPointerDown(callback: (data: PointerEventData) => void): void {
    this.pointerDownCallbacks.push(callback);
  }

  /**
   * Register a callback for pointer up events
   */
  onPointerUp(callback: (data: PointerEventData) => void): void {
    this.pointerUpCallbacks.push(callback);
  }

  /**
   * Register a callback for pointer move events
   */
  onPointerMove(callback: (data: PointerEventData) => void): void {
    this.pointerMoveCallbacks.push(callback);
  }

  /**
   * Remove a pointer down callback
   */
  offPointerDown(callback: (data: PointerEventData) => void): void {
    const index = this.pointerDownCallbacks.indexOf(callback);
    if (index !== -1) this.pointerDownCallbacks.splice(index, 1);
  }

  /**
   * Remove a pointer up callback
   */
  offPointerUp(callback: (data: PointerEventData) => void): void {
    const index = this.pointerUpCallbacks.indexOf(callback);
    if (index !== -1) this.pointerUpCallbacks.splice(index, 1);
  }

  /**
   * Remove a pointer move callback
   */
  offPointerMove(callback: (data: PointerEventData) => void): void {
    const index = this.pointerMoveCallbacks.indexOf(callback);
    if (index !== -1) this.pointerMoveCallbacks.splice(index, 1);
  }

  // Track if current/last mouse event was on canvas
  private _isOnCanvas: boolean = false;

  /**
   * Get pointer event data object
   */
  private getPointerEventData(): PointerEventData {
    return {
      x: this.pointer.x,
      y: this.pointer.y,
      worldX: this.pointer.worldX,
      worldY: this.pointer.worldY,
      leftButtonDown: this.pointer.leftButtonDown,
      rightButtonDown: this.pointer.rightButtonDown,
      button: this.pointer.button,
      isOnCanvas: this._isOnCanvas,
    };
  }

  /**
   * Check if an element is the canvas or a child of the canvas
   */
  private isCanvasElement(target: EventTarget | null): boolean {
    if (!target || !this.canvas) return false;
    if (target === this.canvas) return true;
    // Check if target is inside a React UI element (has data-ui attribute or is in a div with pointer-events)
    if (target instanceof HTMLElement) {
      // Canvas is the only valid target - any other HTML element is UI
      return false;
    }
    return false;
  }

  /**
   * Update - call at end of frame to clear just-pressed/released states
   */
  update(): void {
    // Clear justDown and justUp states
    for (const code of this.justDownSet) {
      const state = this.keys.get(code);
      if (state) state.justDown = false;
    }
    this.justDownSet.clear();

    for (const code of this.justUpSet) {
      const state = this.keys.get(code);
      if (state) state.justUp = false;
    }
    this.justUpSet.clear();
  }

  /**
   * Normalize key name to consistent format
   */
  private normalizeKey(key: string): string {
    // Handle special keys
    if (key === ' ') return 'SPACE';
    if (key === 'Shift' || key === 'ShiftLeft' || key === 'ShiftRight') return 'SHIFT';
    if (key === 'Control' || key === 'ControlLeft' || key === 'ControlRight') return 'CTRL';
    if (key === 'Alt' || key === 'AltLeft' || key === 'AltRight') return 'ALT';
    if (key === 'ArrowUp') return 'ARROWUP';
    if (key === 'ArrowDown') return 'ARROWDOWN';
    if (key === 'ArrowLeft') return 'ARROWLEFT';
    if (key === 'ArrowRight') return 'ARROWRIGHT';
    if (key === 'Enter') return 'ENTER';
    if (key === 'Escape') return 'ESCAPE';
    if (key === 'Tab') return 'TAB';
    if (key === 'Backspace') return 'BACKSPACE';
    return key.toUpperCase();
  }

  /**
   * Handle keydown event
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    if (!this._enabled) return;

    const code = this.normalizeKey(event.key);

    // Update key down set
    if (!this.keyDownSet.has(code)) {
      this.keyDownSet.add(code);
      this.justDownSet.add(code);
    }

    // Update tracked key state
    const state = this.keys.get(code);
    if (state) {
      if (!state.isDown) {
        state.justDown = true;
      }
      state.isDown = true;
      state.isUp = false;
    }
  };

  /**
   * Handle keyup event
   */
  private handleKeyUp = (event: KeyboardEvent): void => {
    if (!this._enabled) return;

    const code = this.normalizeKey(event.key);

    // Update key down set
    this.keyDownSet.delete(code);
    this.justUpSet.add(code);

    // Update tracked key state
    const state = this.keys.get(code);
    if (state) {
      state.isDown = false;
      state.isUp = true;
      state.justUp = true;
    }
  };

  /**
   * Handle mouse move
   */
  private handleMouseMove = (event: MouseEvent): void => {
    if (!this._enabled) return;

    if (this.canvas) {
      const rect = this.canvas.getBoundingClientRect();
      this.pointer.x = event.clientX - rect.left;
      this.pointer.y = event.clientY - rect.top;
    } else {
      this.pointer.x = event.clientX;
      this.pointer.y = event.clientY;
    }

    // Update world position if converters are set
    if (this.getWorldX && this.getWorldY) {
      this.pointer.worldX = this.getWorldX(this.pointer.x);
      this.pointer.worldY = this.getWorldY(this.pointer.y);
    }

    // Call move callbacks
    const eventData = this.getPointerEventData();
    for (const callback of this.pointerMoveCallbacks) {
      callback(eventData);
    }
  };

  /**
   * Handle mouse down
   */
  private handleMouseDown = (event: MouseEvent): void => {
    if (!this._enabled) return;

    // Track if this click is on the canvas
    this._isOnCanvas = this.isCanvasElement(event.target);

    // Clicking the game world should release focus from any HUD text input
    // (e.g. overview search) so combat keys like Q/V keep working.
    if (this._isOnCanvas) {
      const active = document.activeElement;
      if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
        active.blur();
      }
    }

    this.pointer.isDown = true;
    this.pointer.button = event.button;
    this.pointer.leftButtonDown = event.button === 0;
    this.pointer.rightButtonDown = event.button === 2;

    // Call down callbacks
    const eventData = this.getPointerEventData();
    for (const callback of this.pointerDownCallbacks) {
      callback(eventData);
    }
  };

  /**
   * Handle mouse up
   */
  private handleMouseUp = (event: MouseEvent): void => {
    if (!this._enabled) return;

    // Update button state before callbacks
    // Important: Set button to event.button so callbacks know which button was released
    this.pointer.button = event.button;
    if (event.button === 0) {
      this.pointer.leftButtonDown = false;
    } else if (event.button === 2) {
      this.pointer.rightButtonDown = false;
    }

    // Call up callbacks with current state
    const eventData = this.getPointerEventData();
    for (const callback of this.pointerUpCallbacks) {
      callback(eventData);
    }

    // Update overall state after callbacks
    if (!this.pointer.leftButtonDown && !this.pointer.rightButtonDown) {
      this.pointer.isDown = false;
      this.pointer.button = -1;
    }
  };

  /**
   * Handle context menu (prevent default)
   */
  private handleContextMenu = (event: MouseEvent): void => {
    // Prevent default to allow right-click in game
    event.preventDefault();
  };

  /**
   * Destroy and clean up
   */
  destroy(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mousedown', this.handleMouseDown);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('contextmenu', this.handleContextMenu);

    this.keys.clear();
    this.keyDownSet.clear();
    this.justDownSet.clear();
    this.justUpSet.clear();
  }
}
